/**
 * Pure formatters for the project's reference facts.
 *
 * They live here rather than on `BreadcrumbHeader` because #668 split their two
 * readers apart: the header keeps the architect's NAME (who the commercial
 * calls), while the creation date and the architect's initials disc moved down
 * to the Informations page — reference material nobody consults between two
 * client appointments, and, in the disc's case, the page's only avatar.
 */

type Person = {
  first_name?: string | null
  last_name?: string | null
}

/** "JD" — falls back to a neutral placeholder rather than an empty circle. */
export const composeAvatarInitials = (
  person: Person | null | undefined,
): string => {
  if (!person) return '··'
  const first = (person.first_name ?? '').trim().charAt(0).toUpperCase()
  const last = (person.last_name ?? '').trim().charAt(0).toUpperCase()
  return `${first}${last}` || '··'
}

/** `null` when there is no architect, or no name on the one there is. */
export const composeArchitectName = (
  architect: Person | null | undefined,
): string | null => {
  if (!architect) return null
  const name = `${architect.first_name ?? ''} ${architect.last_name ?? ''}`.trim()
  return name || null
}

/** `dd/mm/yyyy`, or `null` for an absent or unparseable timestamp. */
export const formatCreatedDate = (
  created: string | null | undefined,
): string | null => {
  if (!created) return null
  const d = new Date(created)
  if (Number.isNaN(d.getTime())) return null
  return d.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

/**
 * The commercial who owns the dossier. `null` when there is none, or no name
 * on the one there is.
 *
 * Only ever shown to a company: on a salesman's own hub every dossier is
 * theirs, so the line would say the same name on every page.
 */
export const composeSalesmanName = (
  salesman: Person | null | undefined,
): string | null => {
  if (!salesman) return null
  const name = `${salesman.first_name ?? ''} ${salesman.last_name ?? ''}`.trim()
  return name || null
}
